import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime } from 'rxjs/operators';
import { ConsumptionType } from '../../classes/consumption-type.class';
import { Material } from '../../classes/material.class';
import { RawMaterial } from '../../classes/raw-material.class';
import { CodaltComponent } from '../../codalt.component';
import { SelectItemDialogComponent } from '../../components/select-item-dialog/select-item-dialog.component';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { MaterialService } from '../../services/material/material.service';

@Component({
    selector: 'app-materials-management',
    templateUrl: './materials-management.component.html',
    styleUrls: ['./materials-management.component.scss'],
    standalone: false,
})
export class MaterialsManagement extends CodaltComponent implements OnInit, OnDestroy {
    private materialsRaw = signal<Material[]>([]);
    allRawMaterials = signal<RawMaterial[]>([]);
    consumptionTypes = signal<ConsumptionType[]>([]);
    noRawMaterial = signal(false);
    showDeleted = signal(false);

    materials = computed(() => {
        const showDeleted = this.showDeleted();
        const noRawMaterial = this.noRawMaterial();

        return this.materialsRaw()
            .slice()
            .sort((a, b) => {
                const sortName = (n: Material) => {
                    const main = n.raw_materials.find(rm => rm.material_type?.main);
                    return `${main?.name}${n.name}`;
                };
                return sortName(a)?.localeCompare(sortName(b));
            })
            .filter(mat => !!mat.deleted_at === showDeleted)
            .filter(mat =>
                noRawMaterial
                    ? // Waarvan RM zelf verwijderd is
                      mat.raw_materials.filter(rm => !!rm.deleted_at)?.length > 0 || mat.raw_materials?.length === 0
                    : true
            );
    });

    rawMaterialForms = computed(
        () => new Map(this.allRawMaterials().map(rm => [rm.id, this.buildRawMaterialForm(rm)]))
    );

    private rawMaterialsById = computed(() => new Map(this.allRawMaterials().map(rm => [rm.id, rm])));

    constructor(
        private materialService: MaterialService,
        private dialog: MatDialog,
        private confirmDialogService: ConfirmDialogService
    ) {
        super();
    }

    addChooseRawMaterial(material: Material) {
        const items = this.allRawMaterials().map(rm => ({
            ...rm,
            displayLabel: `${rm.id} ${rm.name} ${rm.supplier?.name ?? ''} ${rm.od_number ?? '-'} ${
                rm.color ? `${rm.color.name} (${rm.color.ral})` : ''
            }`,
        }));

        this.subscriptions.add(
            this.dialog
                .open<SelectItemDialogComponent<RawMaterial>, unknown, RawMaterial>(SelectItemDialogComponent, {
                    width: '400px',
                    data: {
                        title: 'Kies een grondstof',
                        items,
                        bindLabel: 'displayLabel',
                        bindValue: 'id',
                        placeholder: 'Grondstof zoeken...',
                    },
                })
                .afterClosed()
                .subscribe(rawMaterial => {
                    if (rawMaterial) {
                        this.addRM(material, rawMaterial.id);
                    }
                })
        );
    }

    addRM(material: Material, rawMaterialId: number) {
        if (!rawMaterialId) return;

        this.materialService.addRawMaterialToMaterial(material.id, rawMaterialId).subscribe({
            next: () => {
                this.reloadMaterials();
            },
            error: error => {
                console.error('Error adding raw material:', error);
            },
        });
    }
    ngOnInit() {
        this.reload();
        this.materialService.getConsumptionTypes().subscribe(consumptionTypes => {
            this.consumptionTypes.set(consumptionTypes);
        });
    }

    // Volledige herlaad, inclusief de grondstoffen-catalogus (allRawMaterials). Die catalogus
    // verandert nooit door acties op deze pagina (toevoegen/verwijderen koppelt alleen een
    // bestaande grondstof), dus alleen hier nodig, niet na elke kleine actie hieronder — anders
    // herbouwt rawMaterialForms alle FormGroups (en hun subscriptions) voor niets, en verlies je
    // eventuele focus/invoer in "Alle grondstoffen".
    reload() {
        this.materialService.getAllRawMaterials().subscribe(rawMaterials => {
            this.allRawMaterials.set(rawMaterials);
        });
        this.reloadMaterials();
    }

    reloadMaterials() {
        this.materialService.getList(true).subscribe(mat => {
            this.materialsRaw.set(mat);
        });
    }

    // De grondstof komt in twee losse API-responses binnen (per materiaal en in "Alle
    // grondstoffen"), dus twee losse object-instanties voor dezelfde grondstof-id. Lees hier
    // altijd de actuele waarde uit de "Alle grondstoffen"-lijst, anders toont deze tabel na het
    // wisselen van verbruikstype daar nog de oude waarde tot een volledige reload.
    consumptionTypeName(rawMaterial: RawMaterial): string | undefined {
        return (
            this.rawMaterialsById().get(rawMaterial.id)?.consumption_type?.name ?? rawMaterial.consumption_type?.name
        );
    }

    cycleConsumptionType(event: MouseEvent, rawMaterial: RawMaterial) {
        event.preventDefault();
        const types = this.consumptionTypes();
        if (!types.length) return;

        const currentIndex = types.findIndex(type => type.id === rawMaterial.consumption_type_id);
        const nextType = types[(currentIndex + 1) % types.length];

        this.materialService.updateRawMaterial(rawMaterial.id, { consumption_type_id: nextType.id }).subscribe({
            next: () => {
                rawMaterial.consumption_type_id = nextType.id;
                rawMaterial.consumption_type = nextType;
            },
            error: error => {
                console.error('Error updating consumption type:', error);
            },
        });
    }

    editRawMaterial(materialId: number, rawMaterialId: number) {
        this.confirmDialogService
            .confirm({
                title: 'Grondstof verwijderen',
                text: 'Weet je zeker dat je deze grondstof wilt verwijderen?',
                confirm: 'Verwijderen',
                cancel: 'Behouden',
            })
            .then(
                () => {
                    this.materialService.deleteRawMaterialFromMaterial(materialId, rawMaterialId).subscribe({
                        next: () => {
                            this.reloadMaterials();
                        },
                        error: error => {
                            console.error('Error deleting raw material:', error);
                        },
                    });
                },
                () => {}
            );
    }

    private buildRawMaterialForm(rawMaterial: RawMaterial): FormGroup {
        const fg = new FormGroup({
            consumption: new FormControl(rawMaterial.consumption),
            thickness: new FormControl(rawMaterial.thickness),
            width: new FormControl(rawMaterial.width),
        });

        this.subscriptions.add(
            fg.valueChanges.pipe(debounceTime(300)).subscribe(values => {
                if (!fg.dirty) return;

                this.materialService.updateRawMaterial(rawMaterial.id, values).subscribe({
                    next: () => {
                        Object.assign(rawMaterial, values);
                        fg.markAsPristine();
                    },
                    error: error => {
                        console.error('Error updating raw material:', error);
                    },
                });
            })
        );

        return fg;
    }

    updatePivotConsumption(pivot: any, value: string) {
        const consumption = value === '' ? null : Number(value);
        this.materialService.updatePivotConsumption(pivot, consumption).subscribe({
            next: () => {
                pivot.consumption = consumption;
            },
            error: error => {
                console.error('Error updating pivot consumption:', error);
            },
        });
    }

    toggleOptional(event: MouseEvent, pivot: any) {
        event.preventDefault();
        const newOptionalValue = pivot.optional === 1 ? 0 : 1;
        this.materialService.toggleOptional(pivot, newOptionalValue).subscribe({
            next: () => {
                this.reloadMaterials();
            },
            error: error => {
                console.error('Error toggling optional:', error);
            },
        });
    }
}
